import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";
import { observer } from "mobx-react-lite";

import { translationKeys } from "../../models/app-translation-keys";
import { applyWhitelabelSettings } from "../../models/functions/apply-whitelabel-settings.function";
import { useCurrentUserService, useSettingService, useWhitelabelService } from "../../services/ServiceHooks";
import { useStore } from "../../stores/StoreContext";
import { MainComponent } from "../Layout";
import { LoadingPage } from "../Pages/FullScreenInformation";

const RawApp = () => {
  const { t } = useTranslation();
  const { userStore, navigationStore, permissionStore } = useStore();
  const location = useLocation();
  const currentUserService = useCurrentUserService();
  const settingSerivce = useSettingService();
  const whitelabelService = useWhitelabelService();

  const [isInitializing, setIsInitializing] = useState<boolean>(true);

  useEffect(() => {
    // Set the title of the page based on the current language
    // Use a timeout to ensure the title is set after the language has been changed
    setTimeout(() => (document.title = t(translationKeys.app.title)), 100);
  }, [userStore.currentLanguage]);

  useEffect(() => {
    if (permissionStore.hasAnyPermission()) {
      navigationStore.setActiveMenuItemBasedOnPath(location.pathname, permissionStore.userPermissions);
    }
  }, [location.pathname, permissionStore.userPermissions]);

  useEffect(() => {
    const initializeGlobalSettings = async () => {
      try {
        applyWhitelabelSettings(await whitelabelService.getGeneralSettings());
        await currentUserService.ensureHasBudget();
        await currentUserService.ensureUserPermissions();
        await settingSerivce.shouldPersistChat();
      } catch (error) {
        console.error("Error initializing global settings:", error);
      } finally {
        setIsInitializing(false);
      }
    };

    if (isInitializing && userStore.userAccessToken) {
      initializeGlobalSettings();
    }
  }, [userStore.userAccessToken]);

  if (isInitializing) {
    return <LoadingPage loadingInformation={t(translationKeys.global.loadingPage)} />;
  }

  return <MainComponent />;
};

export const App = observer(RawApp);
